import { useTranslation } from 'react-i18next';
import { enqueueSnackbar } from 'notistack';
import { useMapLayersState } from '../../state/useMapLayersState';
import {
  MapLayersDetails,
  MapLayer,
  MapLayerDetail,
} from '../../components/Map/constants';
import { KeyboardShortcut } from './useKeyboardShortcut.types';
import { useKeyboardShortcuts } from './useKeyboardShortcuts';

export const useMapLayerKeyboardShortcuts = (): void => {
  const { t } = useTranslation();
  const mapLayersState = useMapLayersState();

  const shortcutLayerGroupsMap = Object.entries(MapLayersDetails)
    .filter(([_, layerDetails]) => layerDetails.keyboardShortcut)
    .reduce<Record<string, [MapLayer, MapLayerDetail][]>>(
      (acc, [layerName, layerDetails]) => {
        const shortcut = layerDetails.keyboardShortcut!;
        acc[shortcut] = acc[shortcut] || [];
        acc[shortcut].push([layerName as MapLayer, layerDetails]);
        return acc;
      },
      {},
    );

  const handlers: Array<KeyboardShortcut> = Object.entries(
    shortcutLayerGroupsMap,
  ).map(([shortcut, layerNamesAndDetails]) => {
    const description = t('keyboardShortcuts.mapLayer.toggleVisibility', {
      layers: layerNamesAndDetails
        // & is getting escaped somewhere, can't figure it out
        .filter((d) => 'titleKey' in d[1])
        // @ts-expect-error
        .map((d) => t(d[1].titleKey))
        .join(', '),
      interpolation: { escapeValue: false },
    });

    return {
      category: 'mapLayers',
      description,
      keys: `l ${shortcut}`,
      handler: () => {
        const anySelected = layerNamesAndDetails.some((detail) =>
          mapLayersState.mapLayers.includes(detail[0]),
        );

        for (const [layerName] of layerNamesAndDetails) {
          mapLayersState.setMapLayerVisibility(layerName, !anySelected);
        }

        const confirmTranslationKey = anySelected
          ? 'keyboardShortcuts.confirmation.off'
          : 'keyboardShortcuts.confirmation.on';

        enqueueSnackbar(
          t(confirmTranslationKey, {
            layer: description,
            interpolation: { escapeValue: false },
          }),
          {
            variant: 'info',
            autoHideDuration: 1000,
          },
        );
      },
    };
  });

  useKeyboardShortcuts(handlers, [mapLayersState.mapLayers]);
};
